import React, { useState, useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { OrderGroup, BatchedChanges, Order, OrderStatus } from '@/types';
import { CardSection, ExpandableCard, ExpandableCardHandle, CardRow, CardDropdownMenu } from '@/components/Cards';
import { DateDisplay } from '@/components/localization';
import OrderList from './OrderList';
import OrderStatusIndicator from '@/components/StatusIndicator/OrderStatusIndicator';
import { useGetActiveProjectId } from '@/utils/hooks';
import HistoryLogModal from '../HistoryLog/HistoryLogModal';
import { faHistory, faFilePdf, faBoxOpen } from '@fortawesome/pro-light-svg-icons';
import { getOrderGroupPdfUrl, reopenOrderGroupOptions } from '@/services/orders';
import { ProjectPermissionToggle } from '@/components/permissions';
import { useLingui } from '@lingui/react';
import { Dropdown, DropdownContextMenu } from '@ygtechnologies/rubus-ui';
import { Toast, YesNoDialog } from '@ygtechnologies/bygr-frontend-component-library';
import { isDev, isTest } from '@/utils/envRenderLogic';

export interface OrderListItemProps {
    orderGroup: OrderGroup;
    defaultExpandedId?: number;
    onChange: (changes: BatchedChanges<Order>) => void;
}

const OrderGroupListItem: React.FC<OrderListItemProps> = ({ orderGroup, defaultExpandedId, onChange }) => {
    const { _ } = useLingui();
    const [isExpanded, setIsExpanded] = useState(!!orderGroup.orders.find((y) => y.id === defaultExpandedId));
    const projectId = useGetActiveProjectId();
    const [logModalIsOpen, setLogModalIsOpen] = useState(false);
    const [reopenOptionsDialogOpen, setReopenOptionsDialogOpen] = useState(false);
    const [total, status, statusCount] = useMemo(() => {
        const nrOfCanceled = orderGroup.orders.filter((x) => x.status === OrderStatus.Canceled).length;
        const nrOfReady = orderGroup.orders.filter((x) => x.status === OrderStatus.ReadyToStart).length;

        const status = nrOfCanceled === 0 ? OrderStatus.ReadyToStart : OrderStatus.Canceled;
        const statusCount = nrOfCanceled !== 0 ? nrOfCanceled : nrOfReady;
        return [orderGroup.orders.length, status, statusCount];
    }, [orderGroup]);

    const handleReopenOrderGroupOptions = () => {
        setReopenOptionsDialogOpen(true);
    };

    const handleReopenOptionsConfirm = async () => {
        const { id } = orderGroup;
        if (!id) return;

        try {
            await reopenOrderGroupOptions(id);
            Toast.success(<Trans>Options for order group have been reopened!</Trans>);
        } catch (e) {
            console.error(e);
            Toast.error(<Trans>Failed to reopen options</Trans>);
        }
    };

    return (
        <ExpandableCard
            wrap
            expanded={isExpanded}
            expandableContent={
                <OrderList orders={orderGroup.orders} defaultExpandedId={defaultExpandedId} onChange={onChange} />
            }>
            <CardRow>
                <CardSection caption={<Trans>Order group</Trans>} width="250px" shrink contentClassName="text-truncate">
                    {orderGroup.deadlineName && <>{orderGroup.deadlineName}</>}
                    {orderGroup.changeRequestName}
                </CardSection>
                <CardSection caption={<Trans>Order items</Trans>} width="100px" grow>
                    {orderGroup.orders.length}
                </CardSection>
                <CardSection caption={<Trans>Signed</Trans>} width="250px" grow>
                    {orderGroup.signature && (
                        <div>
                            <DateDisplay value={orderGroup.signature.signedAt} />
                            <Trans>
                                <small>&nbsp;by {orderGroup.signature.signedByName}</small>
                            </Trans>
                        </div>
                    )}
                </CardSection>
                <CardSection caption={<Trans>Status</Trans>} width="100px" grow>
                    <OrderStatusIndicator value={status}>
                        {statusCount}/{total} {_({ id: `OrderStatus.${status}` })}
                    </OrderStatusIndicator>
                </CardSection>

                <ExpandableCardHandle expanded={isExpanded} onExpandToggle={() => setIsExpanded(!isExpanded)} />
                <DropdownContextMenu>
                    <ProjectPermissionToggle condition="isAdministrator">
                        <Dropdown.Item as="a" icon={faFilePdf} href={getOrderGroupPdfUrl(orderGroup.id)} target="_blank">
                            <Trans>Generate PDF</Trans>
                        </Dropdown.Item>
                    </ProjectPermissionToggle>
                    <Dropdown.Item icon={faHistory} onClick={() => setLogModalIsOpen(true)}>
                        <Trans>View log</Trans>
                    </Dropdown.Item>
                    {/*Only keep this in test and dev for now*/}
                    {(isDev || isTest) && (
                        <Dropdown.Item
                            disabled={status !== OrderStatus.Canceled}
                            icon={faBoxOpen}
                            onClick={() => handleReopenOrderGroupOptions()}>
                            <Trans>Reopen options</Trans>
                        </Dropdown.Item>
                    )}
                </DropdownContextMenu>
            </CardRow>
            {projectId && (
                <HistoryLogModal
                    hiddenColumns={['objectType']}
                    toggle={() => setLogModalIsOpen(false)}
                    isOpen={logModalIsOpen}
                    filter={{
                        projectId: projectId,
                        groupByIds: [orderGroup.id],
                    }}
                />
            )}
            <YesNoDialog
                show={reopenOptionsDialogOpen}
                title={<Trans>Reopen order group options</Trans>}
                body={<Trans>Danger! Are you sure you want to reopen the options for all orders in this group?</Trans>}
                onAccept={handleReopenOptionsConfirm}
                onClose={() => setReopenOptionsDialogOpen(false)}
                confirmButtonText={<Trans>Reopen</Trans>}
            />
        </ExpandableCard>
    );
};

export default OrderGroupListItem;
